import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button } from '@barracuda-internal/bds-core-v2';
import type { CategoryGroup } from 'src/components/Filters/DIConditionCheckboxFilter';
import type { DetectionFilter } from 'src/features/detections/detectionSlice';
import { DETECTION_FILTER_CONDITIONS } from './constants';
import FilterRow from './FilterRow';

type RowType = 'search' | 'filter' | '';

type LocalRow = {
	id: number;
	rowType: RowType;
	scopeField: string;
	condition: string;
	values: string[];
	keyword: string; // Only used when rowType === 'search'
};

let rowIdCounter = 0;

const createEmptyRow = (): LocalRow => ({
	id: ++rowIdCounter,
	rowType: '',
	scopeField: '',
	condition: DETECTION_FILTER_CONDITIONS[0].value,
	values: [],
	keyword: '',
});

interface FilterPopoverContentProps {
	currentSearchField: string;
	currentSearchText: string;
	currentFilters: DetectionFilter[];
	classifierCategories: CategoryGroup[];
	onApply: (
		searchField: string,
		searchText: string,
		filters: DetectionFilter[],
	) => void;
	onClose: () => void;
}

const FilterPopoverContent: React.FC<FilterPopoverContentProps> = ({
	currentSearchField,
	currentSearchText,
	currentFilters,
	classifierCategories,
	onApply,
	onClose,
}) => {
	const [rows, setRows] = useState<LocalRow[]>([]);

	// Initialize rows from current state on mount
	useEffect(() => {
		const initialRows: LocalRow[] = [];

		// Restore search row if there was a search
		if (currentSearchField) {
			initialRows.push({
				id: ++rowIdCounter,
				rowType: 'search',
				scopeField: currentSearchField,
				condition: DETECTION_FILTER_CONDITIONS[0].value,
				values: [],
				keyword: currentSearchText || '',
			});
		}

		// Restore filter rows
		if (currentFilters.length > 0) {
			currentFilters.forEach((f) => {
				initialRows.push({
					id: ++rowIdCounter,
					rowType: 'filter',
					scopeField: f.fieldName,
					condition: f.operation,
					values: [...f.value],
					keyword: '',
				});
			});
		}

		// Always have at least one empty row
		if (initialRows.length === 0) {
			initialRows.push(createEmptyRow());
		}

		setRows(initialRows);
	}, []); // Only on mount

	// Check if search is already used by any row
	const isSearchUsed = useMemo(
		() => rows.some((r) => r.rowType === 'search'),
		[rows],
	);

	// Collect used filter scope values for mutual exclusion
	const usedFilterScopes = useMemo(
		() =>
			rows.filter((r) => r.rowType === 'filter').map((r) => r.scopeField),
		[rows],
	);

	// Count filter rows (for the 3-filter limit)
	const filterRowCount = useMemo(
		() => rows.filter((r) => r.rowType === 'filter').length,
		[rows],
	);

	// Max rows: 1 search + 3 filters = 4
	// Can add if: filter slots remain OR search slot is still available
	const canAddMore = rows.length < 4 && (filterRowCount < 3 || !isSearchUsed);

	const addRow = useCallback(() => {
		if (!canAddMore) return;
		setRows((prev) => [...prev, createEmptyRow()]);
	}, [canAddMore]);

	const removeRow = useCallback((id: number) => {
		setRows((prev) => {
			const updated = prev.filter((r) => r.id !== id);
			return updated.length > 0 ? updated : [createEmptyRow()];
		});
	}, []);

	const updateRowType = useCallback((id: number, rowType: string) => {
		setRows((prev) =>
			prev.map((r) =>
				r.id === id ? { ...r, rowType: rowType as RowType } : r,
			),
		);
	}, []);

	const updateScope = useCallback((id: number, scopeField: string) => {
		setRows((prev) =>
			prev.map((r) => (r.id === id ? { ...r, scopeField } : r)),
		);
	}, []);

	const updateCondition = useCallback((id: number, condition: string) => {
		setRows((prev) =>
			prev.map((r) => (r.id === id ? { ...r, condition } : r)),
		);
	}, []);

	const updateValues = useCallback((id: number, values: string[]) => {
		setRows((prev) =>
			prev.map((r) => (r.id === id ? { ...r, values } : r)),
		);
	}, []);

	const updateKeyword = useCallback((id: number, keyword: string) => {
		setRows((prev) =>
			prev.map((r) => (r.id === id ? { ...r, keyword } : r)),
		);
	}, []);

	const handleApply = useCallback(() => {
		// Extract search data (only UI, not sent as filter)
		const searchRow = rows.find((r) => r.rowType === 'search');
		const searchText = searchRow?.keyword?.trim() || '';
		const searchField = searchText ? searchRow?.scopeField || '' : '';

		// Build DetectionFilter array from filter rows (skip incomplete ones)
		const filters: DetectionFilter[] = rows
			.filter(
				(r) =>
					r.rowType === 'filter' &&
					r.scopeField &&
					r.values.length > 0,
			)
			.map((r) => ({
				fieldName: r.scopeField,
				operation: r.condition,
				value: r.values,
			}));

		onApply(searchField, searchText, filters);
	}, [rows, onApply]);

	const isSearchDisabled = useMemo(() => {
		const searchRow = rows.find((r) => r.rowType === 'search');
		const hasKeyword = searchRow?.scopeField && searchRow?.keyword.trim();
		const hasValidFilter = rows.some(
			(r) =>
				r.rowType === 'filter' && r.scopeField && r.values.length > 0,
		);
		return !hasKeyword && !hasValidFilter;
	}, [rows]);

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: 2,
				width: 850,
			}}
		>
			{/* Unified Rows */}
			{rows.map((row) => (
				<FilterRow
					key={row.id}
					rowType={row.rowType as 'search' | 'filter' | ''}
					scopeField={row.scopeField}
					condition={row.condition}
					values={row.values}
					keyword={row.keyword}
					onRowTypeChange={(t) => updateRowType(row.id, t)}
					onScopeChange={(s) => updateScope(row.id, s)}
					onConditionChange={(c) => updateCondition(row.id, c)}
					onValuesChange={(v) => updateValues(row.id, v)}
					onKeywordChange={(k) => updateKeyword(row.id, k)}
					onAdd={addRow}
					onRemove={() => removeRow(row.id)}
					disableAdd={!canAddMore}
					showRemove={rows.length > 1}
					isSearchUsed={isSearchUsed && row.rowType !== 'search'}
					isFilterLimitReached={
						filterRowCount >= 3 && row.rowType !== 'filter'
					}
					usedFilterScopes={usedFilterScopes}
					classifierCategories={classifierCategories}
				/>
			))}

			{/* Footer */}
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'flex-end',
					gap: 1,
					pt: 1,
					borderTop: '1px solid',
					borderColor: 'divider',
				}}
			>
				<Button bdsType="interactiveNeutral" onClick={onClose}>
					Close
				</Button>
				<Button
					bdsType="interactiveEmphasis"
					onClick={handleApply}
					disabled={isSearchDisabled}
				>
					Search
				</Button>
			</Box>
		</Box>
	);
};

export default FilterPopoverContent;
